import { useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, increment, runTransaction } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { checkRequestOwnership } from '../utils/authUtils';

type User = {
  id: string;
  email: string | null;
  [key: string]: any;
};

export function useAuth() {
  const [user, setUser] = useState<User | null>(() => {
    const storedData = localStorage.getItem('user_data');
    return storedData ? JSON.parse(storedData) : null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        setLoading(true);
        
        if (firebaseUser) {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          const adminDoc = await getDoc(doc(db, 'drawAdmins', firebaseUser.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userState = {
              id: firebaseUser.uid,
              email: firebaseUser.email,
              name: userData.name || userData.username || 'Utilisateur',
              username: userData.username || userData.name || 'Utilisateur',
              country: userData.country || '',
              city: userData.city || '',
              isDrawAdmin: adminDoc.exists(),
              ...userData
            };
            setUser(userState);
            localStorage.setItem('user_data', JSON.stringify(userState));
          } else {
            await firebaseSignOut(auth);
            setUser(null);
            localStorage.removeItem('user_data');
          }
        } else {
          // Ne pas effacer les données si elles existent dans le localStorage
          const storedData = localStorage.getItem('user_data');
          if (!storedData) {
            setUser(null);
            localStorage.removeItem('user_data');
          }
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
        setUser(null);
        localStorage.removeItem('user_data');
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      if (userCredential.user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
          
          // Si l'utilisateur n'existe pas dans Firestore, créer son profil
          if (!userDoc.exists()) {
            await runTransaction(db, async (transaction) => {
              const userRef = doc(db, 'users', userCredential.user.uid);
              const referralCode = localStorage.getItem('referral_code');

              transaction.set(userRef, {
                username: email.split('@')[0],
                usernameLower: email.split('@')[0].toLowerCase(),
                email: email.toLowerCase(),
                referralCode: userCredential.user.uid.slice(0, 8),
                referredBy: referralCode || null,
                referralCount: 0,
                referralEarnings: 0,
                createdAt: new Date()
              });

              // Créer le portefeuille de l'utilisateur
              const walletRef = doc(db, 'wallets', userCredential.user.uid);
              transaction.set(walletRef, {
                balance: 0,
                referralCount: 0,
                referralEarnings: 0,
                investments: [],
                createdAt: new Date()
              });

              // Traiter le parrainage si un code est présent
              if (referralCode) {
                const referralQuery = query(collection(db, 'users'), where('referralCode', '==', referralCode));
                const referralSnapshot = await getDocs(referralQuery);
                
                if (!referralSnapshot.empty) {
                  const referrerDoc = referralSnapshot.docs[0];
                  const referralSettingsRef = doc(db, 'settings', 'referral');
                  const referralSettingsDoc = await transaction.get(referralSettingsRef);
                  const referralSettings = referralSettingsDoc.data();
                  
                  if (referralSettings?.isActive) {
                    const referrerRef = doc(db, 'users', referrerDoc.id);
                    const referrerData = await transaction.get(referrerRef);
                    
                    if (referrerData.exists() && referrerData.data().referralCount < referralSettings.maxReferrals) {
                      // Mettre à jour le parrain
                      transaction.update(referrerRef, {
                        referralCount: increment(1),
                        referralEarnings: increment(referralSettings.reward)
                      });
                      
                      // Mettre à jour le portefeuille du parrain
                      const referrerWalletRef = doc(db, 'wallets', referrerDoc.id);
                      transaction.set(referrerWalletRef, {
                        balance: increment(referralSettings.reward)
                      }, { merge: true });
                    }
                  }
                }
              }
            });
          }

          const adminDoc = await getDoc(doc(db, 'drawAdmins', userCredential.user.uid));
          
          const userData = {
            id: userCredential.user.uid,
            email: userCredential.user.email,
            isDrawAdmin: adminDoc.exists(),
            ...(userDoc.exists() ? userDoc.data() : {})
          };
          
          setUser(userData);
          localStorage.setItem(`user_${userCredential.user.uid}_settings`, JSON.stringify(userData));
        } catch (firestoreError) {
          console.error('Erreur Firestore:', firestoreError);
          throw firestoreError;
        }
      }
    } catch (err: any) {
      console.error('Erreur de connexion:', err);
      if (err.code === 'auth/network-request-failed') {
        setError('Problème de connexion au serveur. Vérifiez votre connexion internet.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Email ou mot de passe incorrect.');
      } else if (err.code === 'auth/user-not-found') {
        setError('Aucun compte trouvé avec cet email.');
      } else {
        setError('Erreur de connexion. Veuillez réessayer.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string, extraData?: any) => {
    try {
      setLoading(true);
      const referralCode = localStorage.getItem('referral_code');

      const usersRef = collection(db, 'users');
      // Vérification avancée pour éviter les doublons de téléphone
      let isPhone = false;
      let phoneValue = '';
      // Si extraData.phone est présent, on vérifie aussi ce champ dans Firestore
      if (extraData && extraData.phone) {
        isPhone = true;
        // On normalise le numéro pour la recherche (on retire les + et espaces)
        phoneValue = extraData.phone.replace(/\D/g, '');
        // Chercher un utilisateur avec ce numéro (normalisé)
        const phoneQuery = query(usersRef, where('phone', '>=', ''), where('phone', '<=', '\uf8ff'));
        const phoneSnapshot = await getDocs(phoneQuery);
        const phoneExists = phoneSnapshot.docs.some(docu => {
          const p = (docu.data().phone || '').replace(/\D/g, '');
          return p === phoneValue;
        });
        if (phoneExists) {
          throw new Error('Ce numéro de téléphone est déjà utilisé');
        }
      }

      // Vérifier d'abord si l'email (ou pseudo-email) existe déjà dans Firestore
      const emailQuery = query(usersRef, where('email', '==', email.toLowerCase()));
      const emailSnapshot = await getDocs(emailQuery);
      if (!emailSnapshot.empty) {
        if (isPhone) {
          throw new Error('Ce numéro de téléphone est déjà utilisé');
        } else {
          throw new Error('Cet email est déjà utilisé');
        }
      }

      // Créer l'utilisateur Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const newReferralCode = user.uid.slice(0, 8);

      try {
        // Créer le document utilisateur dans une transaction
        await runTransaction(db, async (transaction) => {
          // 1. TOUTES LES LECTURES D'ABORD
          let referrerDoc = null;
          let referralSettings = null;
          let referrerData = null;
          
          // Lire les informations de parrainage si nécessaire
          if (referralCode) {
            const referralQuery = query(usersRef, where('referralCode', '==', referralCode));
            const referralSnapshot = await getDocs(referralQuery);
            
            if (!referralSnapshot.empty) {
              referrerDoc = referralSnapshot.docs[0];
              const referralSettingsRef = doc(db, 'settings', 'referral');
              const referralSettingsDoc = await transaction.get(referralSettingsRef);
              referralSettings = referralSettingsDoc.data();
              
              if (referralSettings?.isActive) {
                const referrerRef = doc(db, 'users', referrerDoc.id);
                referrerData = await transaction.get(referrerRef);
              }
            }
          }
          
          // 2. PUIS TOUTES LES ÉCRITURES
          // Créer le document utilisateur
          const userRef = doc(db, 'users', user.uid);
          transaction.set(userRef, {
            username: name,
            usernameLower: name.toLowerCase(),
            email: email.toLowerCase(),
            referralCode: newReferralCode,
            referredBy: referralCode || null,
            referralCount: 0,
            referralEarnings: 0,
            createdAt: new Date(),
            ...(extraData && extraData.phone ? { phone: extraData.phone } : {})
          });
          
          // Créer le portefeuille de l'utilisateur
          const walletRef = doc(db, 'wallets', user.uid);
          transaction.set(walletRef, {
            balance: 0,
            referralCount: 0,
            referralEarnings: 0,
            investments: [],
            createdAt: new Date()
          });
          
          // Mettre à jour le parrain si applicable
          if (referrerDoc && referralSettings?.isActive && referrerData?.exists() && 
              referrerData.data().referralCount < referralSettings.maxReferrals) {
            transaction.update(doc(db, 'users', referrerDoc.id), {
              referralCount: increment(1),
              referralEarnings: increment(referralSettings.reward)
            });
            
            const referrerWalletRef = doc(db, 'wallets', referrerDoc.id);
            transaction.set(referrerWalletRef, {
              balance: increment(referralSettings.reward)
            }, { merge: true });
          }
        });

        return {
          id: user.uid,
          email: user.email,
          username: name
        };
      } catch (error) {
        // Si une erreur se produit après la création de l'utilisateur Auth,
        // supprimer l'utilisateur Auth pour éviter les comptes orphelins
        await user.delete();
        throw error;
      }
    } catch (error: any) {
      console.error("Erreur lors de l'inscription:", error);
      if (error.code === 'auth/email-already-in-use') {
        if (extraData && extraData.phone) {
          throw new Error('Ce numéro de téléphone est déjà utilisé');
        } else {
          throw new Error('Cet email est déjà utilisé');
        }
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setError(null);
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('user_') || key === 'profile_settings') {
          localStorage.removeItem(key);
        }
      });
      window.dispatchEvent(new Event('storage'));
    } catch (err) {
      console.error('Erreur lors de la déconnexion:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la déconnexion');
      throw err;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false
      });
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi du mail de réinitialisation:', error);
      if (error.code === 'auth/user-not-found') {
        throw new Error('Aucun compte associé à cet email');
      }
      throw error;
    }
  };

  const checkRequestAccess = async (requestId: string) => {
    if (!user) return false;
    return await checkRequestOwnership(requestId, user.id);
  };

  return {
    user,
    loading,
    error,
    login,
    register,
    signOut,
    resetPassword,
    checkRequestAccess,
  };
}